/**
 * readinessSlice.js — State for the Owen Readiness experience (`/readiness`).
 *
 * Mirrors the handoff state shape: msgs[] (owen / user{qid} / insight /
 * sideQ / sideA), answers{}, qi, typing, input, sidePending, done, matches,
 * appMode/appDone/si/form/sections, selectedProgram, email/account, modals.
 *
 * Choreography (typing delays, sequencing) lives in
 * `src/components/readiness/useReadinessController.js`; this slice holds
 * only the durable state transitions. The slice is intentionally NOT
 * persisted — each visit starts a fresh conversation (persistence is a
 * backend concern stubbed in readinessService.js).
 */
import { createSlice } from '@reduxjs/toolkit';
import { READINESS_QUESTIONS } from '../config/readinessQuestions.js';
import { buildAppSections } from '../config/readinessAppSections.js';

const initialState = {
  started: false,
  msgs: [],
  answers: {},
  qi: -1,
  typing: false,
  input: '',
  ask: '',
  sidePending: false,
  done: false,
  matches: false,
  review: false,
  // full application phase
  appMode: false,
  appDone: false,
  si: -1,
  form: {},
  sections: {},
  selectedProgram: null,
  // account / modals
  email: '',
  account: false,
  gate: false,
  signin: false,
  signinStep: 0,
  signinError: '',
  code: '',
  report: false,
  reportSent: false,
  leave: false,
};

const formatDigits = (val) => {
  const digits = String(val).replace(/[^0-9]/g, '');
  return digits ? parseInt(digits, 10).toLocaleString() : '';
};

const readinessSlice = createSlice({
  name: 'readiness',
  initialState,
  reducers: {
    markStarted(state) {
      state.started = true;
    },
    setTyping(state, action) {
      state.typing = action.payload;
    },
    /**
     * Payload: string, or { text, qid } when the message asks a question,
     * or { text, demo } for the glowing-avatar demo in the intro.
     */
    appendOwen(state, action) {
      state.typing = false;
      const p = action.payload;
      if (typeof p === 'string') state.msgs.push({ kind: 'owen', text: p });
      else state.msgs.push({ kind: 'owen', text: p.text, qid: p.qid, demo: p.demo });
    },
    appendInsight(state, action) {
      state.typing = false;
      state.msgs.push({ kind: 'insight', text: action.payload });
    },
    appendSideQ(state, action) {
      state.ask = '';
      state.typing = true;
      state.msgs.push({ kind: 'sideQ', text: action.payload });
    },
    appendSideA(state, action) {
      state.typing = false;
      state.msgs.push({ kind: 'sideA', text: action.payload });
    },
    setQi(state, action) {
      state.qi = action.payload;
    },
    setInput(state, action) {
      state.input = formatDigits(action.payload);
    },
    setAsk(state, action) {
      state.ask = action.payload;
    },
    setSidePending(state, action) {
      state.sidePending = action.payload;
    },
    /** User answered the current question: bubble + record + hide composer. */
    recordAnswer(state, action) {
      const { qid, value, label } = action.payload;
      state.answers[qid] = value;
      state.qi = -1;
      state.input = '';
      state.msgs.push({ kind: 'user', text: label, qid });
    },
    /** Rewind the last real answer; recalculates branching downstream. */
    goBack(state) {
      let idx = -1;
      for (let i = state.msgs.length - 1; i >= 0; i--) {
        if (state.msgs[i].kind === 'user' && state.msgs[i].qid) { idx = i; break; }
      }
      if (idx === -1) return;
      const qid = state.msgs[idx].qid;
      delete state.answers[qid];
      const vis = READINESS_QUESTIONS.filter((q) => !q.showIf || q.showIf(state.answers));
      state.qi = vis.findIndex((q) => q.id === qid);
      state.msgs = state.msgs.slice(0, idx);
      state.typing = false;
      state.input = '';
      state.done = false;
      state.sidePending = false;
    },
    /** "I'm all set — let's continue" chip. */
    resumeFromSide(state, action) {
      state.sidePending = false;
      state.msgs.push({ kind: 'user', text: action.payload });
    },
    finishAssessment(state, action) {
      state.typing = false;
      state.done = true;
      state.qi = -1;
      if (!state.selectedProgram) state.selectedProgram = action.payload || null;
    },
    selectProgram(state, action) {
      state.selectedProgram = action.payload;
    },
    gotoMatches(state) {
      state.matches = true;
      state.review = false;
    },
    backToScore(state) {
      state.matches = false;
      state.review = false;
    },
    openReview(state) {
      state.review = true;
      state.matches = false;
    },
    closeReview(state) {
      state.review = false;
    },
    // ── full application ──────────────────────────────────────
    beginApp(state) {
      state.done = false;
      state.matches = false;
      state.review = false;
      state.appMode = true;
      state.si = -1;
      state.form = {};
      state.sidePending = false;
    },
    /**
     * Present a section. If it was saved before (user is revisiting), its
     * values prefill the form so revisiting never wipes earlier answers.
     */
    presentSection(state, action) {
      state.si = action.payload;
      const sec = buildAppSections(state.answers, state.selectedProgram)[action.payload];
      const key = sec ? (sec.id === 'program' ? 'programSpecific' : sec.id) : null;
      const saved = key ? state.sections[key] : null;
      state.form = saved && !saved.skipped ? { ...saved } : {};
    },
    setFormField(state, action) {
      const { key, value, isCurrency } = action.payload;
      state.form[key] = isCurrency ? formatDigits(value) : value;
    },
    /** Save (or skip) the current section; appends the user summary bubble. */
    saveSection(state, action) {
      const { sectionId, title, skipped, filled } = action.payload;
      const key = sectionId === 'program' ? 'programSpecific' : sectionId;
      state.sections[key] = skipped ? { skipped: true } : { ...state.form };
      const label = skipped
        ? 'Skipped \u201c' + title + '\u201d for now'
        : '\u2713 ' + title + ' \u2014 ' + filled + ' detail' + (filled > 1 ? 's' : '') + ' saved';
      state.msgs.push({ kind: 'user', text: label });
      state.si = -1;
      state.form = {};
    },
    finishApp(state) {
      state.done = true;
      state.appDone = true;
      state.appMode = false;
      state.si = -1;
    },
    restart(state, action) {
      const msg = action.payload;
      return {
        ...initialState,
        started: true,
        // The restart message doubles as question 1 — tag it so the
        // glowing learn-more avatar works in the start-over flow too.
        msgs: [{ kind: 'owen', text: msg, qid: 'purpose' }],
        qi: 0,
        email: state.email,
        account: state.account,
      };
    },
    // ── account / modals ──────────────────────────────────────
    setEmail(state, action) {
      state.email = action.payload;
    },
    setAccount(state, action) {
      state.account = action.payload;
      if (!action.payload) {
        state.email = '';
        state.signin = false;
      }
    },
    openGate(state) {
      state.gate = true;
    },
    closeGate(state) {
      state.gate = false;
    },
    openSignin(state) {
      state.signin = true;
      state.signinStep = 0;
      state.signinError = '';
      state.code = '';
    },
    closeSignin(state) {
      state.signin = false;
      state.signinStep = 0;
      state.signinError = '';
      state.code = '';
    },
    setSigninStep(state, action) {
      state.signinStep = action.payload;
      state.signinError = '';
    },
    setSigninError(state, action) {
      state.signinError = action.payload || '';
    },
    setCode(state, action) {
      state.code = String(action.payload).replace(/[^0-9]/g, '').slice(0, 8);
      state.signinError = '';
    },
    signInSuccess(state) {
      state.account = true;
      state.signin = false;
      state.signinStep = 0;
      state.signinError = '';
      state.code = '';
    },
    /** "Leave for homepage" prompt — offers to save progress via email first. */
    openLeave(state) {
      state.leave = true;
    },
    closeLeave(state) {
      state.leave = false;
    },
    openReport(state) {
      state.report = true;
      state.reportSent = false;
    },
    closeReport(state) {
      state.report = false;
    },
    markReportSent(state) {
      state.reportSent = true;
    },
  },
});

export const readinessActions = readinessSlice.actions;
export default readinessSlice.reducer;
